import styled from "@emotion/styled";
import React from "react";

type AlertType =
  | "primary"
  | "secondary"
  | "success"
  | "danger"
  | "warning"
  | "info"
  | "light"
  | "dark";

interface AlertProps {
  type: AlertType;
  children: React.ReactNode;
  trailing?: React.ReactNode;
}

const Alert: React.FC<AlertProps> = ({ type, children, trailing }) => {
  const icon = alertStyles[type].icon;

  return (
    <AlertContainer type={type}>
      <AlertContent>
        {icon !== undefined && <IconSpan>{alertStyles[type].icon}</IconSpan>}
        <div>{children}</div>
      </AlertContent>
      {trailing !== undefined && <Trailing>{trailing}</Trailing>}
    </AlertContainer>
  );
};

const alertStyles: Record<
  AlertType,
  { background: string; border: string; icon?: string; color?: string }
> = {
  primary: { background: "var(--tw-blue-100)", border: "var(--tw-blue-500)" },
  secondary: { background: "var(--tw-gray-100)", border: "var(--tw-gray-500)" },
  success: { background: "var(--tw-green-100)", border: "var(--tw-green-500)", icon: "✅" },
  danger: { background: "var(--tw-red-100)", border: "var(--tw-red-500)", icon: "⛔" },
  warning: { background: "var(--tw-orange-100)", border: "var(--tw-orange-500)", icon: "⚠️" },
  info: { background: "var(--tw-blue-100)", border: "var(--tw-blue-500)", icon: "ℹ️" },
  light: { background: "var(--tw-gray-100)", border: "var(--tw-gray-200)" },
  dark: { background: "var(--tw-gray-800)", border: "var(--tw-gray-900)", color: "white" },
};

const AlertContainer = styled.div<{ type: AlertType }>`
  padding: 1rem;
  border: 1px solid;
  border-radius: 0.5rem;
  background-color: ${({ type }) => alertStyles[type].background};
  border-color: ${({ type }) => alertStyles[type].border};
  color: ${({ type }) => alertStyles[type].color ?? "initial"};
`;

const AlertContent = styled.div`
  display: flex;
  gap: 12px;
`;

const Trailing = styled.div`
  display: flex;
  justify-content: center;
`;

const IconSpan = styled.span`
  font-size: 1.5rem;
`;

export default Alert;
